import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash2, GripVertical, Pencil } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { documentFormApi } from "@/services/api";
import DocumentFormFieldDialog from "./DocumentFormFieldDialog";
import { DocumentOutputField } from "@/models/DocumentOutputFormat";

interface DocumentFormFieldProps {
  field: DocumentOutputField;
  onDeleteField: (fieldId: number) => Promise<void>;
  onUpdateField: (
    fieldId: number,
    updates: Partial<DocumentOutputField>
  ) => Promise<void>;
  dragHandleProps?: any;
  documentId: number;
}

const DocumentFormField = ({
  field,
  onDeleteField,
  onUpdateField,
  dragHandleProps,
  documentId,
}: DocumentFormFieldProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Fetch form data to get available fields for breadcrumb display
  const { data: formData } = useQuery({
    queryKey: ["documentForms", { document_id: documentId }],
    queryFn: () =>
      documentFormApi.list({
        queryKey: ["documentForms", { document_id: documentId }],
      }),
    enabled: field.value_source === "user_input" && !!field.referenced_field_id,
  });

  const handleEditField = async (fieldData: Partial<DocumentOutputField>) => {
    try {
      setIsSubmitting(true);
      await onUpdateField(field.id, fieldData);
      setEditDialogOpen(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error updating field",
        description:
          error.message || "An error occurred while updating the field",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getValueSourceBadge = (source: string) => {
    switch (source) {
      case "user_input":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            User Input
          </span>
        );
      case "predefined":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            Predefined
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  const getReferencedFieldLabel = () => {
    if (field.value_source !== "user_input" || !field.referenced_field_id)
      return "No field selected";

    if (!formData || formData.length === 0) {
      return `Field ID: ${field.referenced_field_id}`;
    }

    const currentForm = formData[0];
    let referencedField = null;
    let stepTitle = "";
    let sectionTitle = "";

    // Find the referenced field in the form structure
    if (currentForm.form_steps) {
      for (const step of currentForm.form_steps) {
        if (step.form_sections) {
          for (const section of step.form_sections) {
            if (section.form_fields) {
              const foundField = section.form_fields.find(
                (formField) => formField.id === field.referenced_field_id
              );
              if (foundField) {
                referencedField = foundField;
                stepTitle = step.step_title || `Step ${step.step_order + 1}`;
                sectionTitle = section.section_title || `Section ${section.id}`;
                break;
              }
            }
          }
          if (referencedField) break;
        }
      }
    }

    if (referencedField) {
      const fieldName =
        referencedField.name ||
        referencedField.label ||
        `field_${referencedField.id}`;
      return `${stepTitle} › ${sectionTitle} › ${fieldName} (${field.referenced_field_id})`;
    }

    return `Field ID: ${field.referenced_field_id}`;
  };

  // Auto-generate name from title if not provided
  const getFieldName = () => {
    if (field.name) {
      return field.name;
    }
    // Auto-generate name from title
    return field.title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .trim();
  };

  return (
    <>
      <div className="p-4 border rounded-md bg-card shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1 space-x-3">
            <div
              {...dragHandleProps}
              className="cursor-grab active:cursor-grabbing mt-1"
              aria-label="Drag to reorder field"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="flex-1 space-y-2">
              {/* Field Title and Source Badge */}
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{field.title}</span>
                {getValueSourceBadge(field.value_source)}
              </div>

              {/* Field Name */}
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">Name:</span>{" "}
                <code className="bg-muted px-1 py-0.5 rounded text-xs">
                  {getFieldName()}
                </code>
                {!field.name && (
                  <span className="text-amber-600 ml-1">(auto-generated)</span>
                )}
              </div>

              {/* Value or Reference */}
              <div className="text-xs text-muted-foreground">
                {field.value_source === "predefined" && field.value && (
                  <div>
                    <span className="font-medium">Value:</span>{" "}
                    <code className="bg-muted px-1 py-0.5 rounded text-xs">
                      {field.value}
                    </code>
                  </div>
                )}
                {field.value_source === "user_input" && (
                  <div>
                    <span className="font-medium">References:</span>{" "}
                    <code className="bg-muted px-1 py-0.5 rounded text-xs">
                      {getReferencedFieldLabel()}
                    </code>
                  </div>
                )}
              </div>

              {/* Comment */}
              {field.comment && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Comment:</span>{" "}
                  <span className="italic">{field.comment}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => setEditDialogOpen(true)}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              disabled={isSubmitting}
            >
              <Pencil className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => onDeleteField(field.id)}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive/90 hover:bg-background"
              disabled={isSubmitting}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <DocumentFormFieldDialog
        isOpen={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleEditField}
        documentId={documentId}
        field={field}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default DocumentFormField;
